import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronsLeft, Plus, Star } from '../../icons';
import { MyStatusAvatar } from '../shared/Avatar';
import { RightRail } from '../RightRail';
import { ChatRoomRailCard } from './ChatRoomRailCard';
import { RailPinnedMenus } from './RailPinnedMenus';
import { ProfilePopover } from '../ProfilePopover';
import { FavoritesPopover, FavoritesRailItems } from '../Favorites';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useAuthStore } from '../../store/authStore';
import { useLayoutShellStore } from '../../store/layoutShellStore';
import { useProfileUiStore } from '../../store/profileUiStore';
import { useMemoPanelStore } from '../../store/memoPanelStore';
import { useAiPanelStore } from '../../store/aiPanelStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { MEMO_PANEL_ENABLED } from '../../lib/memo-flag';
import { SHELL_RIGHTRAIL_GLOBAL, RAIL_FAVORITES_ENABLED, RAIL_MENU_PIN_ENABLED } from '../../lib/layout-shell';
import './RightRailHost.css';

/**
 * ⑤ 우측 사이드 레일 — **전역 공통 요소** (all-frame.html 6a `공통 레이아웃 · 영역 골격`).
 *
 * SSOT 는 "모든 화면이 공유하는 기본 레이아웃 … ⑤ 우측 사이드(프로필·메모·멤버·즐겨찾기)" 라고
 * 규정한다. 종전에는 캘린더만 opt-in 이라 다른 화면에는 레일이 없었는데(design-20260717 §5-⑤ 의
 * 슬롯 규약), 이를 **AppLayout 이 소유하는 상시 요소**로 승격한 것이 본 컴포넌트다.
 *
 * 소유 관계
 *  - 표시 여부: `layoutShellStore.rightRail`('shown'|'hidden') — 사용자 선호라 라우트 전환에도 유지.
 *  - 껍데기·아이템 배치: 기존 `RightRail` 컴포넌트 그대로 재사용(무수정).
 *  - 메모 패널 본체: `AppLayout` 의 `MemoPanel`(전역 1개). 여기서는 토글만 한다.
 *
 * 롤백: `VITE_SHELL_RIGHTRAIL='0'` → 본 호스트 미렌더 + 캘린더의 기존 페이지 내 레일이 되살아난다.
 */
/**
 * 레일 펼침 폭(px) — RightRailHost.css 의 `.app-rightrail.is-open { width }` 와 동기 유지.
 * 2026-08-19 (사용자 지시) — "우측 레일을 72px 로 좌측 레일과 동일한 디자인 구조로".
 * 2026-08-20 (사용자 지시) — 레일 88px · 강조 상자 72px 정사각(확정안 "A 권장").
 * 좌측 레일(`--layout-navbar-w: 88px`)과 같은 값이다. 롤백 플래그가 꺼지면 종전 64px.
 */
// 2026-08-26 (사용자 지시) — 좌·우 레일을 72px 로 맞춘다. 좌측(`--layout-navbar-w`)도 같은 값이다.
//   CSS 짝은 RightRailHost.css 말미의 추가 블록. 둘이 갈라지면 메모 패널이 레일 옆에서 어긋난다.
// 2026-09-07 — 좌측 레일 접힘과 나란히 72 → 56 (정본 F16). CSS 짝 = RightRailHost.css 말미 블록.
const RAIL_WIDTH_PX = RAIL_MENU_PIN_ENABLED ? 56 : 64;
/** 접기/펼치기 전이 시간(ms) — tokens.css `--duration-base` 와 동기 유지. */
const RAIL_ANIM_MS = 200;

export function RightRailHost() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const name = useAuthStore((s) => s.user?.name ?? '');
  const avatarUrl = useAuthStore((s) => s.user?.avatarUrl ?? null);

  /**
   * 아바타 클릭 = 프로필 팝오버(2026-08-12 사용자 요구). `<Link to="/me">` 는 **그대로 둔다** —
   * 기본 이동만 막고(preventDefault) 팝오버를 연다. 이렇게 하면 가운데 클릭·새 탭으로 열기는
   * 종전대로 `/me` 로 가고, 링크 역할(a11y)과 기존 진입 경로도 살아 있다.
   */
  const toggleProfile = useProfileUiStore((s) => s.togglePopover);
  const profileAnchorRef = useRef<HTMLAnchorElement | null>(null);

  const shown = useLayoutShellStore((s) => s.rightRail) === 'shown';
  const setRightRail = useLayoutShellStore((s) => s.setRightRail);
  const toggleMemo = useMemoPanelStore((s) => s.togglePanel);
  /* AI 칸(2026-09-09 · 설계 판 A · 정의서 E2) — 메모와 **같은 계약**이다: 여기서는 토글만 하고
     패널 본체는 `AppLayout` 의 `AiPanel`(전역 1개)이다. 열림 상태를 함께 넘겨 버튼이
     토글로 보이게 한다(패널이 레일을 덮지 않으므로 열려 있는 동안에도 계속 보인다). */
  const toggleAi = useAiPanelStore((s) => s.togglePanel);
  const aiOpen = useAiPanelStore((s) => s.open);

  /**
   * 즐겨찾기(2026-08-18) — 레일 **제일 하단**의 진입점 + 팝오버. 프로필과 같은 계약이라
   * 앵커(버튼)를 실측해 body 로 portal 된 패널을 그 왼쪽에 붙인다.
   * 저장값은 사용자별이므로 로그인 사용자가 정해질 때마다 읽어 온다(같은 사용자면 no-op).
   */
  const favAnchorRef = useRef<HTMLButtonElement | null>(null);
  const favOpen = useFavoritesStore((s) => s.open);
  const toggleFavorites = useFavoritesStore((s) => s.toggle);
  const favCount = useFavoritesStore((s) => s.items.length);
  const hydrateFavorites = useFavoritesStore((s) => s.hydrate);
  useEffect(() => { hydrateFavorites(userId); }, [userId, hydrateFavorites]);

  /**
   * 접기·펼치기 애니메이션 — 앱의 다른 바(NavBar · 캘린더 사이드바 · 메모 패널)와 **같은 폭 전이**.
   * 접는 동안(200ms)은 레일을 DOM 에 유지해야 폭이 줄어드는 모습이 보이므로 useModalLeave 로
   * 언마운트를 늦추고, 펼칠 때는 마운트 직후 1 프레임을 닫힘 상태로 둬(강제 리플로우) 시작 상태를
   * 만들어 준다 — 그래야 열림도 닫힘과 대칭으로 슬라이드한다.
   */
  const { shouldRender, isLeaving } = useModalLeave(shown, RAIL_ANIM_MS);
  const railRef = useRef<HTMLDivElement | null>(null);
  // 초기값 = 현재 표시 상태 — 첫 로드에서 레일이 슬라이드해 들어오지 않게(상시 골격은 그냥 거기 있다).
  // 애니메이션은 사용자가 접고/펴는 순간에만 재생된다.
  const [entered, setEntered] = useState(shown);
  useLayoutEffect(() => {
    if (!shouldRender || isLeaving) { setEntered(false); return; }
    void railRef.current?.getBoundingClientRect().width;
    setEntered(true);
  }, [shouldRender, isLeaving]);

  /**
   * 레일 폭 알림 — 메모 패널이 `right: var(--app-rightrail-w)` 로 레일 옆에 붙는다.
   * 실측(RightRail 의 measureWidthVar)이 아니라 **목표값**을 쓴다: 접히는 중간 폭을 흘리면
   * 패널이 덜덜 떨리기 때문. 목표값을 한 번에 바꾸면 패널은 자기 transition 으로 함께 미끄러진다.
   */
  useEffect(() => {
    if (!SHELL_RIGHTRAIL_GLOBAL) return;
    const root = document.documentElement;
    root.style.setProperty('--app-rightrail-w', shown ? `${RAIL_WIDTH_PX}px` : '0px');
    return () => { root.style.removeProperty('--app-rightrail-w'); };
  }, [shown]);

  if (!SHELL_RIGHTRAIL_GLOBAL) return null;

  // 접힘이 끝나면 화면 우측 경계에 펼치기 손잡이만 남긴다(좌측 SidebarToggle 의 우측 대칭).
  if (!shouldRender) {
    return (
      <button
        type="button"
        className="app-rightrail-expand"
        onClick={() => setRightRail('shown')}
        aria-label="우측 사이드 펼치기"
        title="우측 사이드 펼치기"
      >
        <ChevronsLeft size={14} aria-hidden />
      </button>
    );
  }

  return (
    <div
      ref={railRef}
      /* --wide = 72px + 좌측 레일과 같은 항목 구조(2026-08-19). 플래그가 꺼지면 클래스가 빠져
         종전 64px 규칙만 남는다 — CSS 도 기존 블록은 그대로 두고 아래에 추가만 했다. */
      className={`app-rightrail${RAIL_MENU_PIN_ENABLED ? ' app-rightrail--wide' : ''}${!isLeaving && entered ? ' is-open' : ''}`}
    >
      <RightRail
        profile={
          userId !== null ? (
            <>
              <Link
                to="/me"
                ref={profileAnchorRef}
                className="app-rightrail__profile"
                title="내 프로필"
                aria-label="내 프로필"
                aria-haspopup="dialog"
                onClick={(e) => { e.preventDefault(); toggleProfile(); }}
              >
                {/* 점 색 = 상태바에서 고른 근무 상태(2026-08-12). 미설정이면 종전 초록 그대로. */}
                {/* 2026-08-20 (사용자 지시) — 레일이 88px 로 넓어져 32px 아바타가 작아 보인다:
                    sm(32) → md(40). Avatar 는 한 변을 **인라인 style** 로 심으므로 CSS 로 못 덮는다
                    — 크기 변경은 이 prop 한 곳이 유일한 경로다(tokens.css `--rail-avatar` 는 문서용). */}
                <MyStatusAvatar userId={userId} name={name} src={avatarUrl} size="md" />
              </Link>
              {/* 팝오버는 body 로 portal 된다 — 레일이 overflow:hidden 이라 안에 두면 잘린다. */}
              <ProfilePopover anchorRef={profileAnchorRef} />
            </>
          ) : undefined
        }
        // 폭 변수는 위 effect 가 목표값으로 소유한다(접힘 중 실측값 흘림 방지).
        measureWidthVar={false}
        {...(MEMO_PANEL_ENABLED
          ? { onMemo: toggleMemo }
          // 플래그 OFF 롤백 경로 — 자리만 잡고 비활성(눌리는 척 금지).
          : { memoDisabled: true, memoTitle: '준비 중입니다' })}
        onAi={toggleAi}
        aiActive={aiOpen}
        collapseLabel="우측 사이드 접기"
        // 레일을 접으면 앵커가 사라진다 — 팝오버도 함께 닫는다(앵커 없이 떠 있는 패널 방지).
        onCollapse={() => {
          useProfileUiStore.getState().closePopover();
          useFavoritesStore.getState().close();
          setRightRail('hidden');
        }}
        /* 즐겨찾기 = 레일 **제일 하단**(2026-08-18 사용자 지시). 본문(children)에 두면 채팅방
           카드 같은 항목이 늘어날 때 위로 밀려 올라간다 — 하단 고정 슬롯(footer)이 그 자리다.
           롤백(`VITE_RAIL_FAVORITES='0'`) 시에는 슬롯을 비우고 종전 자리(본문)의 플레이스홀더가
           그대로 산다 — 아래 children 참고. */
        /* 2026-08-19 (사용자 지시) — "우측레일에 기존의 즐겨찾기와 즐겨찾기 팝업은 지운다".
           등록·해제는 전체메뉴의 별(★)이 전담하고, 레일에는 고정된 메뉴만 선다(본문 참조).
           그래서 하단 슬롯을 통째로 비운다 — 롤백 플래그가 꺼졌을 때만 종전 그룹이 되살아난다. */
        footer={RAIL_MENU_PIN_ENABLED ? undefined : RAIL_FAVORITES_ENABLED ? (
          <div className="app-rightrail__favgroup">
            {/* 등록된 즐겨찾기 = 별 버튼 **위**에 하나씩 (2026-08-18 지시). 한 번 눌러 이동한다. */}
            <FavoritesRailItems />
            <div className="app-rightrail__favlabel">즐겨찾기</div>
            <button
              type="button"
              ref={favAnchorRef}
              className={`app-rightrail__favbtn${favOpen ? ' is-open' : ''}`}
              onClick={toggleFavorites}
              aria-haspopup="dialog"
              aria-expanded={favOpen}
              title="즐겨찾기"
              aria-label="즐겨찾기"
            >
              <Star size={15} aria-hidden />
              {favCount > 0 && <span className="app-rightrail__favcount">{favCount}</span>}
            </button>
            {/* 팝오버는 body 로 portal 된다 — 레일이 overflow:hidden 이라 안에 두면 잘린다. */}
            <FavoritesPopover anchorRef={favAnchorRef} />
          </div>
        ) : undefined}
      >
        {/* 채팅방 프로필 — 메모 아래 첫 자리(2026-08-17). 채팅 화면일 때만 스스로 렌더된다. */}
        <ChatRoomRailCard />
        {/* 전체메뉴에서 별(★)로 고정한 메뉴 — 좌측 레일과 같은 모양으로 본문에 선다(2026-08-19).
            본문(children)이 자리인 이유: 좌측 레일도 메뉴가 본문이고, 개수가 늘면 여기만 스크롤된다. */}
        {RAIL_MENU_PIN_ENABLED && <RailPinnedMenus />}
        {/* 즐겨찾기 롤백 경로 — 플래그 OFF 일 때만 종전의 비활성 플레이스홀더가 여기 남는다. */}
        {!RAIL_MENU_PIN_ENABLED && !RAIL_FAVORITES_ENABLED && (
          <>
            <div className="app-rightrail__favlabel">즐겨찾기</div>
            <button
              type="button"
              className="app-rightrail__favadd"
              disabled
              title="준비 중입니다"
              aria-label="즐겨찾기 — 준비 중입니다"
            >
              <Plus size={13} aria-hidden />
            </button>
          </>
        )}
      </RightRail>
    </div>
  );
}
